import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  FiCheck, FiClock, FiX, FiDollarSign, FiImage, 
  FiChevronRight, FiCalendar, FiUser, FiFileText,
  FiAlertCircle, FiCheckCircle, FiLoader
} from 'react-icons/fi';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

// Status configurations
const RESERVATION_STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: FiClock },
  { value: 'approved', label: 'Acceptée', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: FiCheck },
  { value: 'confirmed', label: 'Acompte payé', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: FiCheckCircle },
  { value: 'completed', label: 'Terminée', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: FiCheck },
  { value: 'cancelled', label: 'Annulée', color: 'bg-red-100 text-red-800 border-red-200', icon: FiX },
  { value: 'rejected', label: 'Refusée', color: 'bg-slate-100 text-slate-800 border-slate-200', icon: FiX },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: FiClock },
  { value: 'deposit_paid', label: 'Acompte payé', color: 'bg-blue-100 text-blue-800', icon: FiDollarSign },
  { value: 'fully_paid', label: 'Payé intégralement', color: 'bg-emerald-100 text-emerald-800', icon: FiCheckCircle },
];

const DESIGN_STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-slate-100 text-slate-600', icon: FiClock },
  { value: 'in_progress', label: 'En cours', color: 'bg-blue-100 text-blue-800', icon: FiLoader },
  { value: 'submitted', label: 'Soumis', color: 'bg-amber-100 text-amber-800', icon: FiImage },
  { value: 'approved', label: 'Approuvé', color: 'bg-emerald-100 text-emerald-800', icon: FiCheckCircle },
  { value: 'revision_requested', label: 'Révision demandée', color: 'bg-orange-100 text-orange-800', icon: FiAlertCircle },
];

const getStatusConfig = (statuses, value) => {
  return statuses.find(s => s.value === value) || statuses[0];
};

const StatusBadge = ({ status, statuses }) => {
  const config = getStatusConfig(statuses, status);
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} border flex items-center gap-1.5 px-2.5 py-1`}>
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </Badge>
  );
};

// Status type labels and colors
const STATUS_TYPE_CONFIG = {
  reservation: { label: 'Réservation', color: 'bg-blue-500' },
  payment: { label: 'Paiement', color: 'bg-emerald-500' },
  design: { label: 'Design', color: 'bg-purple-500' }
};

const StatusTimeline = ({ history = [] }) => {
  if (!history || history.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">Aucun historique de statut disponible</p>
    );
  }
  
  return (
    <div className="space-y-3">
      {history.slice().reverse().map((entry, index) => {
        const typeConfig = STATUS_TYPE_CONFIG[entry.status_type] || STATUS_TYPE_CONFIG.reservation;
        return (
          <div key={index} className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-2 h-2 rounded-full ${typeConfig.color} mt-2`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                  {typeConfig.label}
                </span>
                <span className="text-sm font-medium text-slate-900">
                  {entry.from_status || 'Création'} → {entry.to_status}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500">
                  {formatDate(entry.changed_at)}
                </span>
                {entry.changed_by && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <FiUser className="h-3 w-3" />
                    {entry.changed_by}
                  </span>
                )}
              </div>
              {entry.note && (
                <p className="text-xs text-slate-600 mt-1 italic bg-slate-50 px-2 py-1 rounded">
                  "{entry.note}"
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatusManager = ({ 
  reservation, 
  onStatusChange, 
  onPaymentStatusChange,
  onDesignStatusChange,
  isUpdating = false,
  compact = false
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(reservation?.status);
  const [note, setNote] = useState('');
  
  const currentReservationStatus = getStatusConfig(RESERVATION_STATUSES, reservation?.status);
  const currentPaymentStatus = getStatusConfig(PAYMENT_STATUSES, reservation?.payment_status);
  const currentDesignStatus = getStatusConfig(DESIGN_STATUSES, reservation?.design_status);
  
  const handleStatusChange = async (newStatus) => {
    if (newStatus === reservation?.status) return;
    
    try {
      await onStatusChange?.(reservation.id, newStatus, note);
      setNote('');
      toast.success(`Statut mis à jour: ${getStatusConfig(RESERVATION_STATUSES, newStatus).label}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleMarkDepositPaid = async () => {
    try {
      await onPaymentStatusChange?.(reservation.id, 'deposit_paid');
      toast.success('Acompte marqué comme payé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du paiement');
    }
  };

  const handleMarkFullyPaid = async () => {
    try {
      await onPaymentStatusChange?.(reservation.id, 'fully_paid');
      toast.success('Réservation marquée comme payée intégralement');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du paiement');
    }
  };
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={reservation?.status} statuses={RESERVATION_STATUSES} />
        {reservation?.deposit_paid && (
          <StatusBadge status={reservation?.payment_status || 'deposit_paid'} statuses={PAYMENT_STATUSES} />
        )}
        {reservation?.design_status && reservation?.design_status !== 'pending' && (
          <StatusBadge status={reservation?.design_status} statuses={DESIGN_STATUSES} />
        )}
      </div>
    );
  }
  
  return (
    <Card className="border-slate-200" data-testid="status-manager">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FiFileText className="h-4 w-4 text-slate-500" />
          Gestion des Statuts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Status */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Statut de la réservation</label>
          <div className="flex items-center gap-3">
            <Select
              value={reservation?.status}
              onValueChange={handleStatusChange}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[200px]" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESERVATION_STATUSES.map((status) => {
                  const Icon = status.icon;
                  return (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {status.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <StatusBadge status={reservation?.status} statuses={RESERVATION_STATUSES} />
          </div>
        </div>

        {/* Payment Status */}
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <label className="text-sm font-medium text-slate-700">Statut du paiement</label>
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={reservation?.payment_status || 'pending'} statuses={PAYMENT_STATUSES} />
            
            {/* Action buttons based on current payment status */}
            {!reservation?.deposit_paid && reservation?.status === 'approved' && (
              <Button
                size="sm"
                onClick={handleMarkDepositPaid}
                disabled={isUpdating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="mark-deposit-paid"
              >
                <FiDollarSign className="h-4 w-4 mr-1" />
                Marquer acompte reçu
              </Button>
            )}
            
            {reservation?.deposit_paid && !reservation?.balance_paid && (
              <Button
                size="sm"
                onClick={handleMarkFullyPaid}
                disabled={isUpdating}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="mark-fully-paid"
              >
                <FiDollarSign className="h-4 w-4 mr-1" />
                Marquer solde reçu
              </Button>
            )}
            
            {reservation?.payment_status === 'fully_paid' && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <FiCheckCircle className="h-4 w-4" />
                Paiement complet
              </span>
            )}
          </div>
          
          {/* Payment summary */}
          <div className="bg-slate-50 rounded-lg p-3 mt-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">
                  Acompte (fixe):
                </span>
                <span className={`ml-2 font-medium ${reservation?.deposit_paid ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {reservation?.deposit_amount?.toFixed(2) || '100.00'}€
                  {reservation?.deposit_paid && ' ✓'}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Solde:</span>
                <span className={`ml-2 font-medium ${reservation?.balance_paid ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {((reservation?.total_price || 0) - (reservation?.deposit_amount || 100)).toFixed(2)}€
                  {reservation?.balance_paid && ' ✓'}
                </span>
              </div>
              <div className="col-span-2 pt-2 border-t border-slate-200">
                <span className="text-slate-500">Total:</span>
                <span className="ml-2 font-bold text-slate-900">
                  {reservation?.total_price?.toFixed(2) || '0.00'}€
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Design Status */}
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <label className="text-sm font-medium text-slate-700">Statut du design</label>
          <div className="flex items-center gap-3">
            <StatusBadge status={reservation?.design_status || 'pending'} statuses={DESIGN_STATUSES} />
            {reservation?.design_status === 'approved' && (
              <span className="text-sm text-emerald-600">Design validé par le client</span>
            )}
            {reservation?.design_status === 'revision_requested' && (
              <span className="text-sm text-orange-600">Modifications demandées</span>
            )}
          </div>
        </div>

        {/* Status History Toggle */}
        <div className="pt-2 border-t border-slate-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="text-slate-600 hover:text-slate-900 w-full justify-between"
            data-testid="toggle-history"
          >
            <span className="flex items-center gap-2">
              <FiCalendar className="h-4 w-4" />
              Historique des changements
            </span>
            <FiChevronRight className={`h-4 w-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          </Button>
          
          {showHistory && (
            <div className="mt-3 pl-2">
              <StatusTimeline history={reservation?.status_history} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export { StatusManager, StatusBadge, StatusTimeline, RESERVATION_STATUSES, PAYMENT_STATUSES, DESIGN_STATUSES };
export default StatusManager;
